import React, { useEffect } from 'react';

const DonateModal = ({ isOpen, onClose, formUrl = '#' }) => {
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFormClick = () => {
    window.open(formUrl, '_blank');
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm pointer-events-none"
        aria-hidden="true"
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 overflow-hidden pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable Content Wrapper */}
        <div 
          className="max-h-[85vh] overflow-y-auto overscroll-contain custom-scrollbar touch-pan-y"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#9ca3af #f3f4f6',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y'
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="sticky top-4 right-4 float-right w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10 mr-4"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

        {/* Modal Header */}
        <div className="bg-gradient-to-r from-Color-Brand to-Color-Primary px-8 py-6 rounded-t-2xl">
          <h2 className="font-['Montserrat'] font-bold text-2xl md:text-3xl text-white">
            Support Our Legacy
          </h2>
          <p className="font-['Montserrat'] text-sm md:text-base text-white/90 mt-2">
            Your donation helps bright students achieve their dreams
          </p>
        </div>

        {/* Modal Body */}
        <div className="p-6 md:p-8">
          {/* Payment Options Section - QR Code and Bank Details in Row */}
          <div className="flex flex-col lg:flex-row gap-6 mb-8">
            {/* QR Code Section */}
            <div className="flex-shrink-0 bg-gradient-to-br from-Color-Brand/5 to-Color-Primary/5 rounded-xl p-6 border border-Color-Brand/20 flex flex-col items-center justify-center">
              <h3 className="font-['Montserrat'] font-bold text-base md:text-lg text-Color-Brand mb-4 text-center">
                Quick Payment via QR Code
              </h3>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <img 
                  src="/assets/donate-qr.jpg" 
                  alt="Payment QR Code" 
                  className="w-40 h-40 md:w-48 md:h-48 object-contain"
                />
              </div>
              <p className="font-['Montserrat'] text-xs md:text-sm text-gray-600 text-center mt-4">
                Scan with your banking app
              </p>
            </div>

            {/* Bank Details */}
            <div className="flex-1 bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              <h3 className="font-['Montserrat'] font-bold text-lg md:text-xl text-Color-Brand mb-4">
                Bank Account Details
              </h3>
              
              <div className="space-y-3">
                <div className="flex flex-col">
                  <span className="font-['Montserrat'] font-medium text-sm text-gray-600">Account Name</span>
                  <span className="font-['Montserrat'] font-semibold text-base text-gray-900">
                    Faculty of Engineering, University of Ruhuna
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="font-['Montserrat'] font-medium text-sm text-gray-600">Bank</span>
                  <span className="font-['Montserrat'] font-semibold text-base text-gray-900">
                    People's Bank
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="font-['Montserrat'] font-medium text-sm text-gray-600">Branch</span>
                  <span className="font-['Montserrat'] font-semibold text-base text-gray-900">
                    Main Street, Galle
                  </span>
                </div>

                <div className="flex flex-col">
                  <span className="font-['Montserrat'] font-medium text-sm text-gray-600">Account Number</span>
                  <span className="font-['Montserrat'] font-bold text-lg text-Color-Brand">
                    169200101871753
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Donation Form Message */}
          <div className="bg-Color-Brand-Yellow/10 border-l-4 border-Color-Brand-Yellow rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-Color-Brand-Yellow flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-['Montserrat'] font-bold text-base text-gray-900 mb-2">
                  Help Us Recognize Your Contribution
                </h4>
                <p className="font-['Montserrat'] text-sm text-gray-700 leading-relaxed">
                  If you donate for our cause, kindly share your details so we can properly acknowledge and publish your generous donation. Your support will be featured in our Silver Members gallery.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Details Button */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            <button
              onClick={handleFormClick}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-Color-Brand to-Color-Primary hover:from-Color-Primary hover:to-Color-Brand text-white font-['Montserrat'] font-bold text-base rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Submit Your Details
            </button>
            
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-8 py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-['Montserrat'] font-semibold text-base rounded-lg transition-colors duration-300"
            >
              Close
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default DonateModal;
